import { Col, Row, Space } from 'antd';
import { fetchRateDetails } from 'api/products';
import { RATE_TYPES } from 'enum/Rates';
import { get } from 'lodash';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { fetchSelectedVehicleInsurance } from '../../../api/vehicle-insurances';
import { HelpIcon } from '../../../components';
import Box from '../../../components/Box';
import SpaceSpinner from '../../../components/SpaceSpinner';
import StatusTag from '../../../components/Tag/StatusTag';
import Text from '../../../components/Text';
import Toast from '../../../components/Toast';
import UserInsuranceSettingFormContent from '../../../components/UserInsuranceSettingFormContent';
import AxleConnectionStatus from '../../../components/UserInsuranceSettingFormContent/AxleConnectionStatus';
import { STATUS_LIST } from '../../../enum';
import { VEHICLE_INSURANCE_PROVIDERS } from '../../../enum/VehicleInsuranceProviders';
import { formatPageTitle } from '../../../utils/common';
import { selectStoreCurrentAuthUser } from '../../../utils/storeSelectors';

const PageHelmet = () => (
  <Helmet>
    <title>{formatPageTitle('Insurance Info')}</title>
  </Helmet>
);

const UserInsuranceInformationView = props => {
  const {
    t,
    userDetails,
    userRate,
    userComplianceDetails,
    isUserDeleted,
    insuranceCoverages,
  } = props;

  const hasCanadianFavrRate = useMemo(() => {
    return userRate?.country === 'CA' && userRate?.rates?.rateType === RATE_TYPES.FAVR;
  }, [userRate]);

  const rateDetailsQuery = useQuery({
    enabled: !!userRate?.rates?.rateType && userRate?.rates?.rateType !== RATE_TYPES.FAVR,
    queryKey: ['fetchRateDetails', userRate?._id],
    queryFn: () => fetchRateDetails(userRate?._id),
  });

  const insuranceMinimumCoverages = useMemo(() => {
    const rateType = userRate?.rates?.rateType;
    if (rateType === RATE_TYPES.FAVR) {
      return insuranceCoverages;
    }

    const compliance = rateDetailsQuery?.data?.compliance;
    const hasFullInsuranceCompliance =
      compliance?.insuranceCompliance === true && compliance?.insuranceComplianceLevel === 'full';

    if (!hasFullInsuranceCompliance) return undefined;

    const cpmCoverages = rateDetailsQuery?.data?.favrSettings?.insuranceCoverages;
    const hasCoverageAmounts =
      !!cpmCoverages &&
      Object.values(cpmCoverages).some(value => typeof value === 'number' && value > 0);

    return hasCoverageAmounts ? cpmCoverages : undefined;
  }, [insuranceCoverages, rateDetailsQuery]);

  const currentAuthUser = useSelector(selectStoreCurrentAuthUser);

  const selectedVehicleInsuranceQuery = useQuery({
    enabled: !!userDetails._id,
    queryKey: ['fetchSelectedVehicleInsurance', userDetails._id],
    queryFn: () => fetchSelectedVehicleInsurance(userDetails._id),
    retry: false,
    onError: error => {
      if (error?.response?.status === 404) return;

      Toast({
        type: 'error',
        message: t('errorLoadingUserInsurancePolicy'),
      });
    },
  });

  if (selectedVehicleInsuranceQuery.isFetching) {
    return (
      <Box>
        <PageHelmet />
        <SpaceSpinner />
      </Box>
    );
  }

  return (
    <Space direction="vertical" size="middle">
      <Row justify="end" align="bottom">
        <Col>
          {selectedVehicleInsuranceQuery.data?.provider === VEHICLE_INSURANCE_PROVIDERS.AXLE && (
            <AxleConnectionStatus
              t={t}
              axleConnectionInfo={get(selectedVehicleInsuranceQuery.data, 'connection')}
              policyRefreshedAt={get(selectedVehicleInsuranceQuery.data, 'updatedAt')}
            />
          )}
        </Col>
      </Row>

      <Box>
        <PageHelmet />

        <Space direction="vertical" size="small">
          <Row align="middle" gutter={[16, 16]}>
            <Col>
              <Text variant="h5" style={{ margin: 0 }}>
                {t('insurancePolicyInfo')}
              </Text>
            </Col>

            {userComplianceDetails?.insuranceComplianceStatus && (
              <Col>
                <StatusTag status={userComplianceDetails.insuranceComplianceStatus} />
              </Col>
            )}

            {userComplianceDetails?.insuranceComplianceStatus === STATUS_LIST().Status.APPROVED && (
              <Col>
                <HelpIcon variant="info" hint={t('modifyingInsuranceDriverLicenseWarning')} />
              </Col>
            )}
          </Row>

          <UserInsuranceSettingFormContent
            t={t}
            disabled={isUserDeleted || userDetails._id !== currentAuthUser.profile._id}
            insuranceCoverages={insuranceMinimumCoverages}
            canUpdateInsuranceDoc={userDetails._id === currentAuthUser.profile._id}
            selectedVehicleInsurance={selectedVehicleInsuranceQuery.data}
            userComplianceDetails={userComplianceDetails}
            user={userDetails}
            hasCanadianFavrRate={hasCanadianFavrRate}
          />
        </Space>
      </Box>
    </Space>
  );
};

export default UserInsuranceInformationView;
